/**
 * Selectable bordered card used for credit-pack choices.
 *
 * The prototype drew this two ways: the Top-up page used a `<label onClick>` with a
 * hand-drawn circle and NO input element, so it was mouse-only and unannounced;
 * the out-of-credits modal used a real radio. This keeps the hand-drawn circle
 * (which is the design) but puts a real radio input behind it, visually hidden yet
 * focusable — so keyboard and screen-reader users get the native radio-group
 * behaviour while the pixels are unchanged.
 *
 * Wrap a set of these in `<RadioCardGroup>` so they form one group.
 */
import type { CSSProperties, ReactNode } from 'react';
import { c } from '../../styles/theme';

export type RadioCardSize = 'default' | 'compact';

export interface RadioCardProps {
  /** Shared across the group; identifies the radio set. */
  name: string;
  value: string;
  checked: boolean;
  onSelect: (value: string) => void;
  /** Main content, left-aligned. */
  children: ReactNode;
  /** Right-aligned content, typically the price. */
  trailing?: ReactNode;
  size?: RadioCardSize;
  /** Accessible name, when `children` is markup rather than plain text. */
  ariaLabel?: string;
  style?: CSSProperties;
}

/** Off-screen but focusable — display:none or visibility:hidden would remove it from the tab order. */
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

export default function RadioCard({
  name,
  value,
  checked,
  onSelect,
  children,
  trailing,
  size = 'default',
  ariaLabel,
  style,
}: RadioCardProps) {
  const compact = size === 'compact';

  return (
    <label
      style={{
        position: 'relative',
        border: checked ? `2px solid ${c.accentBorder}` : `1px solid ${c.border}`,
        borderRadius: compact ? 8 : 10,
        padding: compact ? '8px 10px' : '12px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        cursor: 'pointer',
        background: compact ? undefined : c.surface1,
        ...style,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          type="radio"
          name={name}
          value={value}
          checked={checked}
          onChange={() => onSelect(value)}
          aria-label={ariaLabel}
          style={VISUALLY_HIDDEN}
        />
        <span
          aria-hidden="true"
          style={{
            width: 15,
            height: 15,
            borderRadius: '50%',
            border: `1.5px solid ${checked ? c.accentFill : c.borderStrong}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {checked && (
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.accentFill }} />
          )}
        </span>
        <span>{children}</span>
      </span>
      {trailing}
    </label>
  );
}

export interface RadioCardGroupProps {
  /** Names the group for assistive tech, e.g. "Credit pack". */
  label: string;
  children: ReactNode;
  gap?: number;
  marginBottom?: number;
}

/** Groups RadioCards so they are announced as one set of choices. */
export function RadioCardGroup({ label, children, gap = 8, marginBottom }: RadioCardGroupProps) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      style={{ display: 'flex', flexDirection: 'column', gap, marginBottom }}
    >
      {children}
    </div>
  );
}
